import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useForm } from '@mantine/form';
import {
    Button,
    SegmentedControl,
    Textarea,
    Text,
    Badge,
    NumberInput,
    Flex,
    Checkbox,
    Grid,
    Box,
    Accordion,
} from '@mantine/core';
import { modals } from '@mantine/modals';
import { IconCalendar, IconMapPin } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { DatePickerInput } from '@mantine/dates';

import { CurrentPrices, Request, Store } from '../../codegen/graphql';
import {
    BELOW_COST_NEW_PRICE_ERROR,
    getExVatPrice,
} from '../../helpers/calculateMargin';
import { getHighestCostPrice } from '../../helpers/priceCalculation';
import MarginCalculation from './components/MarginCalculation';
import { StoreSelectionItem } from './components/StoreSelectionItem';
import RestrictedStoresNotice from './components/RestrictedStoresNotice';
import {
    determineRequiredPriceTypes,
    shouldDisableCashbuildPriceInput,
    shouldDisablePnlPriceInput,
    shouldShowCashbuildPriceInput,
    shouldShowPnlPriceInput,
} from '../../helpers/storeHelpers';
import { getCurrencyFromRequest } from '../../utility/getProductCurrency';
import { useEffectiveCostPrices } from '../../hooks/useEffectiveCostPrices';
import { getRequestEffectiveCostPrice } from '../../helpers/requestZonePricing';
import {
    filterStoreIdsToEligibleSet,
    isStoreSelected,
    parseApplicableTo,
} from '../../helpers/storeSelectionHelper';
import { useHighestCostPriceStore } from '../../hooks/useHighestCostPriceStore';
import { useEligibleAdvertisingZoneStores } from '../../hooks/useEligibleAdvertisingZoneStores';
import { When } from '../common';
import { useAppConfig } from '../../hooks/useAppConfig';
import { getErrorMessage } from '../../helpers/getErrorMessage';
import {
    getPriceChangeScope,
    getPriceChangeScopeLabel,
    getSuggestedStoreIds,
    PRICE_CHANGE_SCOPE,
} from '../../helpers/priceChangeScope';

const APPLICABLE_TO_OPTIONS = [
    { value: 'zone', label: 'Advertising Zone' },
    { value: 'store', label: 'Store' },
];

const getApplicableToOptions = (lockToZone: boolean) => {
    if (lockToZone) {
        return [APPLICABLE_TO_OPTIONS[0]];
    }
    return APPLICABLE_TO_OPTIONS;
};

type Props = {
    request: Request & { resolvedVatPercentage?: number | null };
    store: Store | null;
    advertisingZoneStores: Store[];
    currentPrices?: CurrentPrices;
    close: () => void;
    onConfirm: (input: {
        outcome: string;
        notes: string;
        metadata: {
            changedPrice?: number;
            changedPriceValidFrom?: string;
            changedPriceValidTo?: string;
            applyToStores?: string[];
            applyImmediately?: boolean;
            pnlPrice?: number;
            cashbuildPrice?: number;
        };
    }) => void;
};

function AcceptPriceChangeRequestForm({
    request,
    store,
    advertisingZoneStores,
    currentPrices,
    onConfirm,
    close,
}: Props) {
    const isFromSurvey = Boolean(request?.isFromPriceSurvey);
    const {
        eligibleZoneStores,
        eligibleZoneStoreIds,
        hasEligibleZoneStores,
        defaultApplicableTo,
    } = useEligibleAdvertisingZoneStores(advertisingZoneStores);
    const lockApplicableToZone = isFromSurvey && hasEligibleZoneStores;
    const applicableToDirtyRef = useRef(false);
    const selectedStoresDirtyRef = useRef(false);
    const { vatPercentage: fallbackVatPercentage } = useAppConfig();
    const resolvedVatPercentage =
        request?.resolvedVatPercentage ?? fallbackVatPercentage;

    const stores = useMemo(() => {
        if (hasEligibleZoneStores) {
            return eligibleZoneStores;
        }
        if (store) {
            return [store];
        }
        return [];
    }, [hasEligibleZoneStores, eligibleZoneStores, store]);

    const highestCostPriceStoreId =
        useHighestCostPriceStore(eligibleZoneStores);
    const totalEligible = eligibleZoneStores.length;
    const dmSuggestedScope = getPriceChangeScope(request);
    const dmSuggestedStoreIds = getSuggestedStoreIds(request);
    const dmSuggestedScopeLabel = getPriceChangeScopeLabel(dmSuggestedScope);

    const suggestedEligibleStoreIds = useMemo(
        () =>
            filterStoreIdsToEligibleSet(
                dmSuggestedStoreIds,
                eligibleZoneStores,
            ),
        [dmSuggestedStoreIds, eligibleZoneStores],
    );

    let initialApplicableTo = defaultApplicableTo;
    if (lockApplicableToZone) {
        initialApplicableTo = 'zone';
    } else if (
        dmSuggestedScope === PRICE_CHANGE_SCOPE.STORE ||
        !hasEligibleZoneStores
    ) {
        initialApplicableTo = 'store';
    }

    const initialSelectedStores =
        suggestedEligibleStoreIds.length > 0
            ? suggestedEligibleStoreIds
            : eligibleZoneStoreIds;

    const validatePriceChangeDurationEnd = (
        endDate: Date | null,
        noEndDate: boolean,
    ): string | null => {
        if (noEndDate) {
            return null;
        }
        if (endDate) {
            return null;
        }
        return 'End date is required.';
    };

    const validateNewPrice = (
        value: number | undefined,
        needsCashbuildPrice: boolean,
        costPrice: number | null,
    ): string | null => {
        if (!needsCashbuildPrice) {
            return null;
        }
        return getExVatPrice(value ?? 0, resolvedVatPercentage) >=
            (costPrice || 0)
            ? null
            : BELOW_COST_NEW_PRICE_ERROR;
    };

    const validatePnlPrice = (
        value: number | undefined | null,
        needsPnlPrice: boolean,
        isTradingAsCashbuild: boolean,
        costPrice: number | null,
    ): string | null => {
        if (value === undefined || value === null) {
            return null;
        }
        if (isTradingAsCashbuild) {
            return null;
        }
        if (!needsPnlPrice) {
            return null;
        }
        return getExVatPrice(value, resolvedVatPercentage) >= (costPrice || 0)
            ? null
            : 'P&L price (ex VAT) must be more than the cost price.';
    };

    const form = useForm<{
        applicableTo: string;
        newPrice: number | undefined;
        pnlPrice: number | undefined;
        notes: string;
        priceChangeDurationStart: Date;
        priceChangeDurationEnd: Date | null;
        noEndDate: boolean;
        immediatePriceChange: boolean;
        selectedStores: string[];
    }>({
        initialValues: {
            applicableTo: initialApplicableTo,
            newPrice: request?.proposedPrice,
            pnlPrice: request?.proposedPrice,
            notes: '',
            priceChangeDurationStart: request?.proposedValidFrom
                ? new Date(request.proposedValidFrom)
                : dayjs().add(1, 'day').toDate(),
            priceChangeDurationEnd: null,
            noEndDate: false,
            immediatePriceChange: false,
            selectedStores: initialSelectedStores,
        },
        validate: values => {
            let selectedStoresList: typeof stores = [];
            if (values.applicableTo === 'zone') {
                selectedStoresList = eligibleZoneStores.filter(zoneStore =>
                    values.selectedStores.includes(zoneStore.id),
                );
            } else if (store) {
                selectedStoresList = [store];
            }

            const zoneHasStores =
                values.applicableTo !== 'zone' ||
                filterStoreIdsToEligibleSet(
                    values.selectedStores,
                    eligibleZoneStores,
                ).length > 0;

            const { needsCashbuildPrice, needsPnlPrice } =
                determineRequiredPriceTypes(selectedStoresList);
            const costPriceForValidation =
                selectedStoresList.length > 0
                    ? getHighestCostPrice(selectedStoresList)
                    : getRequestEffectiveCostPrice(request ?? {});

            return {
                applicableTo: zoneHasStores
                    ? null
                    : 'Select at least one store in the zone.',
                newPrice: validateNewPrice(
                    values.newPrice,
                    needsCashbuildPrice,
                    costPriceForValidation,
                ),
                pnlPrice: validatePnlPrice(
                    values.pnlPrice,
                    needsPnlPrice,
                    store?.isTradingAsCashbuild ?? false,
                    costPriceForValidation,
                ),
                priceChangeDurationStart: values.priceChangeDurationStart
                    ? null
                    : 'Start date is required.',
                priceChangeDurationEnd: validatePriceChangeDurationEnd(
                    values.priceChangeDurationEnd,
                    values.noEndDate,
                ),
            };
        },
        validateInputOnBlur: true,
        validateInputOnChange: ['newPrice'],
    });

    const { effectiveStores, cashbuildCostPrice, pnlCostPrice } =
        useEffectiveCostPrices({
            applicableTo: form.values.applicableTo,
            selectedStoreIds: form.values.selectedStores,
            stores,
            store,
            fallbackCostPrice: getRequestEffectiveCostPrice(request ?? {}),
        });

    const priceTypeInfo = useMemo(() => {
        return determineRequiredPriceTypes(effectiveStores);
    }, [effectiveStores]);

    useEffect(() => {
        if (!applicableToDirtyRef.current) {
            if (form.values.applicableTo !== initialApplicableTo) {
                form.setFieldValue('applicableTo', initialApplicableTo);
            }
        }

        if (!hasEligibleZoneStores) {
            return;
        }

        if (!selectedStoresDirtyRef.current) {
            form.setFieldValue('selectedStores', initialSelectedStores);
            return;
        }

        const filteredSelectedStores = filterStoreIdsToEligibleSet(
            form.values.selectedStores,
            eligibleZoneStores,
        );
        if (
            filteredSelectedStores.length !== form.values.selectedStores.length
        ) {
            form.setFieldValue('selectedStores', filteredSelectedStores);
        }
    }, [
        eligibleZoneStoreIds,
        eligibleZoneStores,
        hasEligibleZoneStores,
        initialApplicableTo,
        initialSelectedStores,
    ]);

    useEffect(() => {
        if (form.values.newPrice !== undefined) {
            form.validateField('newPrice');
        }
        if (
            form.values.pnlPrice !== undefined &&
            form.values.pnlPrice !== null
        ) {
            form.validateField('pnlPrice');
        }
    }, [priceTypeInfo]);

    const onChangeSelectedStore = useCallback(
        (storeId: string, selected: boolean) => {
            selectedStoresDirtyRef.current = true;
            const { selectedStores } = form.getValues();
            const currentSelected = selectedStores || [];
            if (selected) {
                form.setFieldValue(
                    'selectedStores',
                    currentSelected.concat(storeId),
                );
                return;
            }
            form.setFieldValue(
                'selectedStores',
                currentSelected.filter(id => id !== storeId),
            );
        },
        [form],
    );

    const productCurrency = useMemo(
        () => getCurrencyFromRequest(request),
        [request],
    );

    const renderDynamicContent = useCallback(() => {
        const applicableToStore = form.values.applicableTo === 'store';

        return (
            <div className="flex flex-col gap-2">
                <RestrictedStoresNotice
                    advertisingZoneStores={advertisingZoneStores}
                />
                <When
                    condition={applicableToStore}
                    fallback={
                        <Accordion
                            classNames={{
                                control: 'pl-0 pr-0',
                                item: 'border-none',
                            }}
                        >
                            <Accordion.Item value="collapsible">
                                <Accordion.Control>
                                    <Text>
                                        {form.values.selectedStores.length ===
                                        totalEligible
                                            ? `All ${form.values.selectedStores.length}`
                                            : `Selected ${form.values.selectedStores.length}`}{' '}
                                        stores in Advertising Zone
                                    </Text>
                                </Accordion.Control>
                                <Accordion.Panel>
                                    {eligibleZoneStores.map(zoneStore => (
                                        <StoreSelectionItem
                                            key={zoneStore.id}
                                            store={zoneStore}
                                            isSelected={isStoreSelected(
                                                zoneStore.id,
                                                form.values.selectedStores,
                                            )}
                                            onToggle={onChangeSelectedStore}
                                            isHighestCostPrice={
                                                zoneStore.id ===
                                                highestCostPriceStoreId
                                            }
                                        />
                                    ))}
                                </Accordion.Panel>
                            </Accordion.Item>
                        </Accordion>
                    }
                >
                    <>
                        <Text>Applying to Store</Text>
                        <div className="flex gap-2 items-center">
                            <IconMapPin size={18} />
                            <Text className="text-[#4D5E80] Capitalize">
                                {store?.name || request?.storeName || 'N/A'}
                            </Text>
                            <Badge size="sm" variant="light">
                                {store?.storeNo || request?.storeNo || 'N/A'}
                            </Badge>
                        </div>
                    </>
                </When>
            </div>
        );
    }, [
        form.values.applicableTo,
        form.values.selectedStores,
        store,
        request,
        eligibleZoneStores,
        highestCostPriceStoreId,
        totalEligible,
        onChangeSelectedStore,
    ]);

    const handleClose = () => {
        form.reset();
        close();
    };

    const openConfirmZoneApplicationModal = () => {
        modals.openConfirmModal({
            id: 'confirm-zone-application',
            title: 'Apply Change To the Advertising Zone?',
            modalId: 'confirm-zone-application',
            children: (
                <div className="p-2">
                    <Text>
                        Are you sure you want to apply this change request to
                        all the selected stores in the advertising zone?
                    </Text>
                </div>
            ),
            onConfirm: () => {
                const selectedStoresList = eligibleZoneStores.filter(
                    zoneStore =>
                        form.values.selectedStores.includes(zoneStore.id),
                );

                const { needsCashbuildPrice, needsPnlPrice } =
                    determineRequiredPriceTypes(selectedStoresList);

                let cashbuildPrice: number | undefined;
                let pnlPrice: number | undefined;

                if (needsCashbuildPrice) {
                    cashbuildPrice = Number(form.values.newPrice);
                }

                if (needsPnlPrice) {
                    pnlPrice = form.values.pnlPrice
                        ? Number(form.values.pnlPrice)
                        : undefined;
                }

                onConfirm({
                    outcome: 'Approved',
                    notes: form.values.notes,
                    metadata: {
                        cashbuildPrice,
                        pnlPrice,
                        changedPriceValidFrom: dayjs(
                            form.values.priceChangeDurationStart || new Date(),
                        ).toISOString(),
                        changedPriceValidTo: dayjs(
                            form.values.priceChangeDurationEnd || new Date(),
                        ).toISOString(),
                        applyImmediately: form.values.immediatePriceChange,
                        applyToStores: filterStoreIdsToEligibleSet(
                            form.values.selectedStores,
                            eligibleZoneStores,
                        ),
                    },
                });
                handleClose();
            },

            classNames: {
                title: 'text-primary font-semibold text-lg',
                body: 'p-4',
                inner: 'z-[1200]',
                overlay: 'z-[1000]',
            },
            labels: {
                confirm: 'Confirm',
                cancel: 'Cancel',
            },

            confirmProps: {
                fullWidth: false,
                size: 'md',
            },
            cancelProps: {
                variant: 'outline',
                fullWidth: false,
                size: 'md',
            },
        });
    };

    const onChangeNoEndDate = (event: React.ChangeEvent<HTMLInputElement>) => {
        form.setFieldValue('noEndDate', event.currentTarget.checked);
        const nextEndDate: Date | null = event.currentTarget.checked
            ? dayjs('9999-12-31').toDate()
            : null;
        form.setFieldValue('priceChangeDurationEnd', nextEndDate);
    };

    const handleSubmit = form.onSubmit(values => {
        try {
            if (values.applicableTo === 'zone') {
                openConfirmZoneApplicationModal();
                return;
            }

            const selectedStoresList = store ? [store] : [];

            const { needsCashbuildPrice, needsPnlPrice } =
                determineRequiredPriceTypes(selectedStoresList);

            let cashbuildPrice: number | undefined;
            let pnlPrice: number | undefined;

            if (needsCashbuildPrice) {
                cashbuildPrice = Number(values.newPrice);
            }

            if (needsPnlPrice) {
                pnlPrice = values.pnlPrice
                    ? Number(values.pnlPrice)
                    : undefined;
            }

            onConfirm({
                outcome: 'Approved',
                notes: values.notes,
                metadata: {
                    changedPrice: Number(values.newPrice),
                    cashbuildPrice,
                    pnlPrice,
                    changedPriceValidFrom: dayjs(
                        values.priceChangeDurationStart || new Date(),
                    ).toISOString(),
                    changedPriceValidTo: dayjs(
                        values.priceChangeDurationEnd || new Date(),
                    ).toISOString(),
                    applyImmediately: values.immediatePriceChange,
                },
            });

            handleClose();
        } catch (error: unknown) {
            form.setFieldError('notes', getErrorMessage(error));
        }
    });

    const isStoreLevel = form.values.applicableTo === 'store';
    const isCashbuildDisabled = isStoreLevel
        ? shouldDisableCashbuildPriceInput({ store })
        : !form.values.selectedStores.some(id => {
              const selectedStore = stores.find(st => st.id === id);
              return (
                  !selectedStore?.storeNo?.startsWith('P') ||
                  selectedStore?.isTradingAsCashbuild
              );
          });
    const isPnlDisabled = isStoreLevel
        ? !(store?.storeNo?.startsWith('P') ?? false) ||
          (store?.isTradingAsCashbuild ?? false)
        : shouldDisablePnlPriceInput(
              stores.filter(s => form.values.selectedStores.includes(s.id)),
          );
    const shouldShowCashbuild = isStoreLevel
        ? !isCashbuildDisabled
        : shouldShowCashbuildPriceInput(effectiveStores);
    const shouldShowPnl = isStoreLevel
        ? !isPnlDisabled
        : shouldShowPnlPriceInput(effectiveStores);
    const showSingleInput =
        (isStoreLevel && (isCashbuildDisabled || isPnlDisabled)) ||
        (!isStoreLevel && (!shouldShowCashbuild || !shouldShowPnl));

    return (
        <form onSubmit={handleSubmit} className="flex flex-col gap-5 p-4">
            <div>
                <When condition={hasEligibleZoneStores}>
                    <>
                        <Text color="#212529">Adjustment Applicable To</Text>
                        <SegmentedControl
                            data={getApplicableToOptions(lockApplicableToZone)}
                            fullWidth
                            color="primary"
                            classNames={{
                                root: 'mt-1',
                            }}
                            disabled={lockApplicableToZone}
                            {...form.getInputProps('applicableTo')}
                            onChange={value => {
                                applicableToDirtyRef.current = true;
                                const nextApplicableTo =
                                    parseApplicableTo(value);
                                if (!nextApplicableTo) {
                                    return;
                                }
                                form.setFieldValue(
                                    'applicableTo',
                                    nextApplicableTo,
                                );
                            }}
                        />
                    </>
                </When>
                <When condition={Boolean(dmSuggestedScopeLabel)}>
                    <Text size="sm" c="dimmed" mt={4}>
                        DM suggested: {dmSuggestedScopeLabel}
                    </Text>
                </When>
                <When condition={Boolean(form.errors?.applicableTo)}>
                    <Text c="#212529" className="mt-2 text-red-500 text-sm">
                        {form.errors.applicableTo}
                    </Text>
                </When>
                <When
                    condition={
                        form.values.applicableTo === 'zone' &&
                        !hasEligibleZoneStores
                    }
                >
                    <Text size="sm" c="dimmed" mt={4}>
                        {request?.storeNo?.startsWith('P')
                            ? `Applying to Store: ${request.storeName} (${request.storeNo})`
                            : 'No stores in Advertising Zone'}
                    </Text>
                </When>
            </div>
            {renderDynamicContent()}
            <Grid>
                <When condition={shouldShowCashbuild}>
                    <Grid.Col span={showSingleInput ? 12 : 6}>
                        <NumberInput
                            label="New Cashbuild Price"
                            decimalScale={2}
                            required
                            min={0}
                            step={0.1}
                            disabled={!isStoreLevel && isCashbuildDisabled}
                            {...form.getInputProps('newPrice')}
                        />
                        <Box mt="md">
                            <MarginCalculation
                                costPrice={cashbuildCostPrice}
                                currentSellingPrice={request?.currentPrice ?? 0}
                                proposedPrice={form.values.newPrice ?? 0}
                                benchmarkMargin={request?.benchmarkMargin ?? 0}
                                currency={productCurrency}
                                completed={request?.completed}
                                previousCostPrice={request?.previousCostPrice}
                                previousSellingPrice={
                                    request?.previousSellingPrice
                                }
                                previousCashbuildPrice={
                                    request?.previousCashbuildPrice
                                }
                                previousPnlPrice={request?.previousPnlPrice}
                                priceType="cashbuild"
                                currentPrices={currentPrices}
                                layout={showSingleInput ? 'row' : 'column'}
                                vatPercentage={resolvedVatPercentage}
                            />
                        </Box>
                    </Grid.Col>
                </When>

                <When condition={shouldShowPnl}>
                    <Grid.Col span={showSingleInput ? 12 : 6}>
                        <NumberInput
                            label="New P&L Price"
                            decimalScale={2}
                            min={0}
                            step={0.1}
                            disabled={!isStoreLevel && isPnlDisabled}
                            {...form.getInputProps('pnlPrice')}
                        />
                        <Box mt="md">
                            <MarginCalculation
                                costPrice={pnlCostPrice}
                                currentSellingPrice={request?.currentPrice ?? 0}
                                proposedPrice={form.values.pnlPrice || 0}
                                benchmarkMargin={request?.benchmarkMargin ?? 0}
                                currency={productCurrency}
                                completed={request?.completed}
                                previousCostPrice={request?.previousCostPrice}
                                previousSellingPrice={
                                    request?.previousSellingPrice
                                }
                                previousCashbuildPrice={
                                    request?.previousCashbuildPrice
                                }
                                previousPnlPrice={request?.previousPnlPrice}
                                priceType="pnl"
                                currentPrices={currentPrices}
                                layout={showSingleInput ? 'row' : 'column'}
                                vatPercentage={resolvedVatPercentage}
                            />
                        </Box>
                    </Grid.Col>
                </When>
            </Grid>

            <Flex gap={18}>
                <DatePickerInput
                    size="md"
                    radius="lg"
                    required
                    minDate={new Date()}
                    label="Price Start Date"
                    popoverProps={{
                        withinPortal: true,
                        zIndex: 999,
                        position: 'top',
                    }}
                    placeholder="Pick start date"
                    className="flex-1"
                    leftSection={<IconCalendar size={18} />}
                    {...form.getInputProps('priceChangeDurationStart')}
                />
                <Flex direction="column" gap={12} className="flex-1">
                    <DatePickerInput
                        size="md"
                        radius="lg"
                        required
                        minDate={
                            dayjs(form.values.priceChangeDurationStart)
                                .add(1, 'day')
                                .toDate() || new Date()
                        }
                        label="Price End Date"
                        popoverProps={{
                            withinPortal: true,
                            zIndex: 999,
                            position: 'top',
                        }}
                        disabled={form.values.noEndDate}
                        placeholder="Pick end date"
                        leftSection={<IconCalendar size={18} />}
                        {...form.getInputProps('priceChangeDurationEnd')}
                    />
                    <Checkbox
                        label="No end date"
                        {...form.getInputProps('noEndDate')}
                        onChange={onChangeNoEndDate}
                    />
                </Flex>
            </Flex>

            <Textarea
                size="md"
                radius="lg"
                label="Notes"
                placeholder="Additional Notes"
                classNames={{
                    label: 'text-[16px]',
                }}
                {...form.getInputProps('notes')}
            />

            <When
                condition={dayjs(form.values.priceChangeDurationStart).isSame(
                    dayjs(),
                    'day',
                )}
            >
                <Checkbox
                    label="Apply price change immediately"
                    {...form.getInputProps('immediatePriceChange')}
                />
            </When>

            <div className="flex gap-4">
                <Button size="md" variant="outline" onClick={handleClose}>
                    Cancel
                </Button>
                <Button size="md" type="submit">
                    Proceed
                </Button>
            </div>
        </form>
    );
}

export default AcceptPriceChangeRequestForm;
